import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BoxIcon, ChevronRightIcon } from 'lucide-react';
import { EncabezadoPanel, EstadoVacio, Panel } from '../ui/Basicos';
import { ChipEstado, ChipMateria } from '../ui/Etiquetas';
import { ESTILO_OCUPACION, nivelOcupacion, type NodoAlmacen } from '../../utils/almacen';
function BarraOcupacion({
  ocupado,
  capacidad
}: {
  ocupado: number;
  capacidad: number;
}) {
  const pct = Math.min(100, Math.round(ocupado / capacidad * 100));
  const estilo = ESTILO_OCUPACION[nivelOcupacion(ocupado, capacidad)];
  return <div className="mt-2" data-id="element-119" data-mp="32yym42r07h5a">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gris-200" data-id="element-120" data-mp="2xwzj4tz6em8v">
        <div className={`h-full rounded-full ${estilo.barra}`} style={{
        width: `${pct}%`
      }} data-id="element-121" data-mp="2syejavnu64ky" />
      </div>
      <p className={`tabular mt-1 text-2xs ${estilo.texto}`} data-id="element-122" data-mp="w5w7c1djv1mq">
        {ocupado}/{capacidad} · {estilo.etiqueta}
      </p>
    </div>;
}
function Columna({
  titulo,
  items,
  activo,
  onSelect,
  pie
}: {
  titulo: string;
  items: {
    clave: string;
    etiqueta: string;
    detalle: string;
  }[];
  activo: string;
  onSelect: (i: number) => void;
  pie?: React.ReactNode;
}) {
  return <div className="min-w-0 flex-1 border-r border-gris-200 last:border-r-0" data-id="element-123" data-mp="3d2q4rrle8jl5">
      <p className="border-b border-gris-200 bg-gris-50 px-4 py-2 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-124" data-mp="2z74ivtagifwy">
        {titulo}
      </p>
      <ul className="max-h-72 divide-y divide-gris-100 overflow-y-auto scroll-fino" data-id="element-125" data-mp="3rtp92qf74lyx">
        {items.map((item, i) => <li key={item.clave} data-id="element-126" data-mp="mcriaaei6ih" data-mpi={i}>
            <button type="button" onClick={() => onSelect(i)} className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors duration-150 ${activo === item.clave ? 'bg-marino-50' : 'hover:bg-gris-50'}`} data-id="element-127" data-mp="pqr5arok1m8">
              <span className="min-w-0" data-id="element-128" data-mp="jdvuih4fdkki">
                <span className={`block truncate text-sm ${activo === item.clave ? 'font-semibold text-marino-800' : 'font-medium text-gris-700'}`} data-id="element-129" data-mp="jexdr8vlhddb">
                  {item.etiqueta}
                </span>
                <span className="block text-2xs text-gris-400" data-id="element-130" data-mp="je1a5gsz1rbs">{item.detalle}</span>
              </span>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-gris-300" aria-hidden="true" data-id="element-131" data-mp="fcpiz6ug8y1h" />
            </button>
          </li>)}
      </ul>
      {pie && <div className="border-t border-gris-200 px-3 py-2" data-id="element-132" data-mp="5jidzn3mfk1x">{pie}</div>}
    </div>;
}
export function MapaAlmacen({
  almacenes,
  pieAlmacen,
  piePasillo,
  pieEstante,
  pieCaja
}: {
  almacenes: NodoAlmacen[];
  pieAlmacen?: React.ReactNode;
  piePasillo?: (almacenId: string) => React.ReactNode;
  pieEstante?: (almacenId: string, pasilloId: string) => React.ReactNode;
  pieCaja?: (almacenId: string, pasilloId: string, estanteId: string) => React.ReactNode;
}) {
  const [almacenSel, setAlmacenSel] = useState(0);
  const [pasilloSel, setPasilloSel] = useState(0);
  const [estanteSel, setEstanteSel] = useState(0);
  const [cajaSel, setCajaSel] = useState<string | null>(null);
  if (almacenes.length === 0) {
    return <Panel data-id="element-133" data-mp="27aijyz3luxsj">
        <EstadoVacio mensaje="Esta sucursal no tiene almacenes configurados" sugerencia="Agrega un almacén desde Configuración → Sucursales y almacenes." data-id="element-134" data-mp="277wgbaju52fl" />
      </Panel>;
  }
  const almacen = almacenes[Math.min(almacenSel, almacenes.length - 1)];
  const pasillo = almacen.pasillos[Math.min(pasilloSel, Math.max(0, almacen.pasillos.length - 1))];
  const estante = pasillo?.estantes[Math.min(estanteSel, Math.max(0, pasillo.estantes.length - 1))];
  const caja = estante?.cajas.find(c => c.id === cajaSel) ?? estante?.cajas[0];
  return <>
      <Panel data-id="element-135" data-mp="vuagmvgcnift">
        <div className="flex flex-col divide-y divide-gris-200 lg:flex-row lg:divide-y-0" data-id="element-136" data-mp="vshglz08rwgs">
          <Columna titulo="Almacén" items={almacenes.map(a => ({
          clave: a.id,
          etiqueta: a.nombre,
          detalle: `${a.pasillos.length} pasillos`
        }))} activo={almacen.id} onSelect={i => {
          setAlmacenSel(i);
          setPasilloSel(0);
          setEstanteSel(0);
          setCajaSel(null);
        }} pie={pieAlmacen} data-id="element-137" data-mp="10rkdgw62iwdo" />
          <Columna titulo="Pasillo" items={almacen.pasillos.map(p => ({
          clave: p.id,
          etiqueta: p.nombre,
          detalle: `${p.estantes.length} estantes`
        }))} activo={pasillo?.id ?? ''} onSelect={i => {
          setPasilloSel(i);
          setEstanteSel(0);
          setCajaSel(null);
        }} pie={piePasillo?.(almacen.id)} data-id="element-138" data-mp="3m5ijjfniiwsi" />
          <Columna titulo="Estante" items={(pasillo?.estantes ?? []).map(e => ({
          clave: e.id,
          etiqueta: e.nombre,
          detalle: `${e.cajas.length} cajas`
        }))} activo={estante?.id ?? ''} onSelect={i => {
          setEstanteSel(i);
          setCajaSel(null);
        }} pie={pasillo ? pieEstante?.(almacen.id, pasillo.id) : undefined} data-id="element-139" data-mp="1ws5bpgg6yqmn" />
        </div>
      </Panel>

      <div className="mt-4 grid gap-4 xl:grid-cols-3" data-id="element-140" data-mp="1307nkqlelcts">
        <Panel className="xl:col-span-2" data-id="element-141" data-mp="12ymtt36wxxwz">
          <EncabezadoPanel titulo={`Cajas · ${almacen.nombre} / ${pasillo?.nombre ?? '—'} / ${estante?.nombre ?? '—'}`} descripcion="El color de la barra indica la ocupación: azul con espacio, ámbar casi llena, rojo llena." accion={pasillo && estante ? pieCaja?.(almacen.id, pasillo.id, estante.id) : undefined} data-id="element-142" data-mp="17yst95jfcopm" />
          {!estante || estante.cajas.length === 0 ? <EstadoVacio mensaje="Este estante todavía no tiene cajas" data-id="element-143" data-mp="2tbvhuqdy07yo" /> : <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-144" data-mp="2qyi8q1hlhrov">
              {estante.cajas.map(c => {
            const activa = c.id === caja?.id;
            const estilo = ESTILO_OCUPACION[nivelOcupacion(c.expedientes.length, c.capacidad)];
            return <button key={c.id} type="button" onClick={() => setCajaSel(c.id)} className={`rounded-lg border px-4 py-3 text-left transition-colors duration-150 ${activa ? 'border-marino-700 bg-marino-50' : 'border-gris-200 bg-white hover:border-marino-300'}`} data-id="element-145" data-mp="2cyzco72266sx">
                    <div className="flex items-center justify-between gap-2" data-id="element-146" data-mp="3tc7e9zfx9km7">
                      <span className="flex items-center gap-2 text-sm font-semibold text-marino-800" data-id="element-147" data-mp="3teaefcasgu4n">
                        <BoxIcon className="h-4 w-4 text-gris-400" aria-hidden="true" data-id="element-148" data-mp="3ojnzx09eulgc" />
                        {c.nombre}
                      </span>
                      <span className={`text-2xs font-semibold ${estilo.texto}`} data-id="element-149" data-mp="4rdq0p7tw89e">
                        {estilo.etiqueta}
                      </span>
                    </div>
                    <BarraOcupacion ocupado={c.expedientes.length} capacidad={c.capacidad} data-id="element-150" data-mp="ep1lxih67lt5" />
                  </button>;
          })}
            </div>}
        </Panel>

        <Panel data-id="element-151" data-mp="nowynrqdjova">
          <EncabezadoPanel titulo={`Contenido de ${caja?.nombre ?? 'la caja'}`} descripcion={`${caja?.expedientes.length ?? 0} expedientes resguardados`} data-id="element-152" data-mp="noevsc97icee" />
          {!caja || caja.expedientes.length === 0 ? <EstadoVacio mensaje="Caja vacía" sugerencia="Puedes asignar expedientes a esta caja al registrarlos o reubicarlos." data-id="element-153" data-mp="xh2r12kjg8b0" /> : <ul className="divide-y divide-gris-100" data-id="element-154" data-mp="16fxysj38dqgf">
              {caja.expedientes.map(e => <li key={e.id} className="px-5 py-3" data-id="element-155" data-mp="1rotjf7yi5hgc">
                  <div className="flex items-center justify-between gap-2" data-id="element-156" data-mp="1npt2juzl4ovz">
                    <Link to={`/expedientes/${e.id}`} className="font-mono text-xs font-semibold text-marino-700 hover:underline" data-id="element-157" data-mp="21osubbie9jwd">
                      {e.localizador}
                    </Link>
                    <ChipMateria materia={e.materia} data-id="element-158" data-mp="190es0jk9w1f0" />
                  </div>
                  <p className="mt-1 truncate text-xs text-gris-700" data-id="element-159" data-mp="1423loit6s91j">{e.cliente}</p>
                  <div className="mt-1.5 flex items-center gap-2" data-id="element-160" data-mp="1iq81nf0uu6gm">
                    <ChipEstado estado={e.estado} data-id="element-161" data-mp="1dz86ofbapygr" />
                    {e.prestadoA && <span className="text-2xs font-medium text-ambar-700" data-id="element-162" data-mp="1dxexotd7n25l">
                        Fuera: {e.prestadoA}
                      </span>}
                  </div>
                </li>)}
            </ul>}
        </Panel>
      </div>
    </>;
}